import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedChars } from "@/components/motion/Reveal";
import ScrambleText from "@/components/motion/ScrambleText";
import Magnetic from "@/components/motion/Magnetic";
import { heroLines, heroIntro } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden pt-24 sm:pt-28 md:pt-32 lg:pt-40">
      <span
        aria-hidden="true"
        className="stroke-ink pointer-events-none absolute -right-8 top-10 select-none font-display text-[38vw] font-extrabold leading-none opacity-[0.05] md:top-0 md:text-[26vw]"
      >
        #
      </span>
      <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 gap-8 px-5 pb-12 sm:px-8 md:px-10 lg:px-12 xl:px-16 lg:grid-cols-12 lg:gap-6 lg:pb-16">
        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-muted"
          >
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3, duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="h-px w-10 origin-left bg-accent"
            />
            <ScrambleText text="Integrated Brand & Communications" />
          </motion.p>

          <MaskedChars
            lines={heroLines}
            delay={0.25}
            className="font-display text-[14vw] font-extrabold leading-[0.88] tracking-tighter text-ink md:text-[8.5vw]"
          />
        </div>

        <div className="flex flex-col justify-end lg:col-span-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-sm text-base leading-relaxed text-muted md:text-lg"
          >
            {heroIntro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Magnetic>
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="btn-fill group inline-flex items-center gap-2 bg-accent px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-bone"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Discuss a Project <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Magnetic>
            <Magnetic>
              <Link
                to="/work"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-ink px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bone"
              >
                View Our Work
              </Link>
            </Magnetic>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, clipPath: "inset(12% 6% 12% 6%)" }}
        animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ delay: 1.05, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
        className="relative mx-auto max-w-[1600px] overflow-hidden px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16"
      >
        <div className="relative h-[52vh] overflow-hidden md:h-[72vh]">
          <motion.img
            src="/images/hero-studio.webp"
            alt="HashTag — brand and communications work"
            fetchPriority="high"
            decoding="async"
            style={{ y, scale }}
            className="absolute inset-0 h-[120%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/10" />
          <span className="absolute bottom-5 left-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-bone md:bottom-8 md:left-8">
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
              <ArrowDown size={14} />
            </motion.span>
            Scroll
          </span>
        </div>
      </motion.div>
    </section>
  );
}
